/**
 * Day Editor
 *
 * Interactive hourly bar editor for editing daily schedule values.
 * Supports click and drag to adjust values.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import type { TimeValue, DayType, ScheduleTypeLimits } from './types';

/**
 * Format a value for display (removes unnecessary decimal places)
 */
function formatValue(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Convert time values to hourly array for display (24 values)
 */
function timeValuesToHourlyArray(values: TimeValue[]): number[] {
  const hourly = new Array(24).fill(0) as number[];

  if (values.length === 0) return hourly;

  // Sort values by time
  const sorted = [...values].sort((a, b) => {
    const aMinutes = a.hour * 60 + a.minute;
    const bMinutes = b.hour * 60 + b.minute;
    return aMinutes - bMinutes;
  });

  // Fill in values
  let currentValue = 0;
  let valueIndex = 0;

  for (let hour = 0; hour < 24; hour++) {
    // Find the value that applies to this hour
    while (valueIndex < sorted.length) {
      const tv = sorted[valueIndex];
      if (!tv) break;
      const tvHour = tv.hour === 24 ? 24 : tv.hour;
      const tvMinutes = tvHour * 60 + tv.minute;
      const hourEnd = (hour + 1) * 60;

      if (tvMinutes <= hourEnd) {
        currentValue = tv.value;
        if (tvMinutes <= hour * 60) {
          valueIndex++;
        } else {
          break;
        }
      } else {
        break;
      }
    }
    hourly[hour] = currentValue;
  }

  return hourly;
}

/**
 * Convert hourly array back to time values
 */
function hourlyArrayToTimeValues(hourly: number[]): TimeValue[] {
  const values: TimeValue[] = [];
  let currentValue: number | null = null;

  for (let hour = 0; hour < 24; hour++) {
    const value = hourly[hour];
    if (value !== currentValue) {
      if (currentValue !== null) {
        // Only add if there's a change in value
      }
      values.push({ hour: hour + 1, minute: 0, value: value ?? 0 });
      currentValue = value ?? 0;
    }
  }

  // Ensure we end at 24:00 if the last value is different
  if (values.length === 0) {
    values.push({ hour: 24, minute: 0, value: hourly[23] ?? 0 });
  } else {
    const lastValue = values[values.length - 1];
    if (lastValue && lastValue.hour !== 24) {
      values.push({ hour: 24, minute: 0, value: lastValue.value });
    }
  }

  return values;
}

/**
 * Optimize time values by merging consecutive identical values
 */
function optimizeTimeValues(values: TimeValue[]): TimeValue[] {
  if (values.length <= 1) return values;

  const optimized: TimeValue[] = [];
  let prevValue: number | null = null;

  for (const tv of values) {
    if (tv.value !== prevValue) {
      optimized.push(tv);
      prevValue = tv.value;
    } else {
      // Update the last entry's time instead
      const last = optimized[optimized.length - 1];
      if (last) {
        last.hour = tv.hour;
        last.minute = tv.minute;
      }
    }
  }

  return optimized;
}

interface DayEditorProps {
  /** Current time values */
  values: TimeValue[];
  /** Day types this schedule applies to */
  dayTypes: DayType[];
  /** Callback when values change */
  onChange: (values: TimeValue[]) => void;
  /** Schedule type limits for bounds checking */
  typeLimits?: ScheduleTypeLimits | null;
  /** Value snap increment (e.g., 0.1, 0.05) */
  snapIncrement?: number;
  /** Whether the editor is read-only */
  readOnly?: boolean;
}

/**
 * Day Editor Component
 */
export function DayEditor({
  values,
  dayTypes,
  onChange,
  typeLimits,
  snapIncrement = 0.1,
  readOnly = false,
}: DayEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHour, setDragHour] = useState<number | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);
  const [hourlyValues, setHourlyValues] = useState<number[]>(() =>
    timeValuesToHourlyArray(values)
  );

  // Update hourly values when input values change
  useEffect(() => {
    setHourlyValues(timeValuesToHourlyArray(values));
  }, [values]);

  // Calculate min/max from type limits
  const minValue = typeLimits?.lowerLimit ?? 0;
  const maxValue = typeLimits?.upperLimit ?? 1;
  const valueRange = maxValue - minValue;

  // Format day types for display
  const dayTypesLabel = useMemo(() => {
    if (dayTypes.includes('AllDays')) return 'All Days';
    if (dayTypes.includes('Weekdays')) return 'Weekdays';
    if (dayTypes.includes('Weekends')) return 'Weekends';
    return dayTypes.join(', ');
  }, [dayTypes]);

  // Calculate bar height percentage
  const getBarHeight = useCallback(
    (value: number) => {
      if (valueRange === 0) return 100;
      return Math.max(0, Math.min(100, ((value - minValue) / valueRange) * 100));
    },
    [minValue, valueRange]
  );

  // Calculate value from Y position
  const getValueFromY = useCallback(
    (clientY: number, rect: DOMRect) => {
      const relativeY = clientY - rect.top;
      const percentage = 1 - relativeY / rect.height;
      let value = minValue + percentage * valueRange;

      // Snap to increment
      value = Math.round(value / snapIncrement) * snapIncrement;

      // Clamp to bounds
      return Math.max(minValue, Math.min(maxValue, value));
    },
    [minValue, maxValue, valueRange, snapIncrement]
  );

  // Get hour from X position
  const getHourFromX = useCallback((clientX: number, rect: DOMRect) => {
    const relativeX = clientX - rect.left;
    const percentage = relativeX / rect.width;
    return Math.max(0, Math.min(23, Math.floor(percentage * 24)));
  }, []);

  // Handle mouse down on bar
  const handleMouseDown = useCallback(
    (event: React.MouseEvent, hour: number) => {
      if (readOnly) return;

      event.preventDefault();
      setIsDragging(true);

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const value = getValueFromY(event.clientY, rect);
      const newHourly = [...hourlyValues];
      newHourly[hour] = value;
      setHourlyValues(newHourly);
      setDragHour(hour);
      setDragValue(value);
    },
    [readOnly, hourlyValues, getValueFromY]
  );

  // Handle mouse move while dragging
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!isDragging || readOnly) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const hour = getHourFromX(event.clientX, rect);
      const value = getValueFromY(event.clientY, rect);
      const newHourly = [...hourlyValues];
      newHourly[hour] = value;
      setHourlyValues(newHourly);
      setDragHour(hour);
      setDragValue(value);
    },
    [isDragging, readOnly, hourlyValues, getHourFromX, getValueFromY]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDragHour(null);
      setDragValue(null);
      // Convert hourly values to time values and notify parent
      const timeValues = hourlyArrayToTimeValues(hourlyValues);
      const optimized = optimizeTimeValues(timeValues);
      onChange(optimized);
    }
  }, [isDragging, hourlyValues, onChange]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      handleMouseUp();
    }
  }, [isDragging, handleMouseUp]);

  // Get bar color based on value
  const getBarColor = useCallback(
    (value: number) => {
      const percentage = valueRange > 0 ? (value - minValue) / valueRange : 0;
      // Gradient from blue (low) to orange (high)
      if (percentage > 0.7) return 'bg-orange-500';
      if (percentage > 0.4) return 'bg-amber-400';
      if (percentage > 0.1) return 'bg-blue-400';
      return 'bg-blue-300';
    },
    [minValue, valueRange]
  );

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div>
          <h3 className="text-sm font-medium text-foreground">{dayTypesLabel}</h3>
          <p className="text-xs text-muted-foreground">Click and drag to adjust values</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {/* Live value indicator during drag */}
          {isDragging && dragHour !== null && dragValue !== null && (
            <span className="rounded bg-primary/10 px-2 py-0.5 font-medium text-primary" aria-live="polite">
              {String(dragHour).padStart(2, '0')}:00 = {formatValue(dragValue)}
            </span>
          )}
          <span>Snap:</span>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono">{snapIncrement}</span>
        </div>
      </div>

      {/* Chart area */}
      <div className="relative p-4">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-4 bottom-8 flex w-8 flex-col justify-between text-right text-xs text-muted-foreground">
          <span>{maxValue}</span>
          <span>{((maxValue + minValue) / 2).toFixed(1)}</span>
          <span>{minValue}</span>
        </div>

        {/* Bar chart */}
        <div
          ref={containerRef}
          className={`relative ml-10 h-48 ${readOnly ? '' : 'cursor-crosshair'}`}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          role="application"
          aria-label="Hourly schedule editor. Click and drag to adjust values."
        >
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="border-t border-border/50"
                style={{ top: `${(i / 4) * 100}%` }}
              />
            ))}
          </div>

          {/* Bars */}
          <div className="absolute inset-0 flex" role="group" aria-label="Hourly values">
            {hourlyValues.map((value, hour) => {
              const heightPercent = getBarHeight(value);
              const isActive = isDragging && dragHour === hour;
              return (
                <div
                  key={hour}
                  className="flex-1 flex flex-col justify-end px-px"
                  onMouseDown={(e) => handleMouseDown(e, hour)}
                >
                  <div
                    className={`${getBarColor(value)} rounded-t transition-all duration-75 relative ${
                      readOnly ? '' : 'hover:opacity-80'
                    } ${isActive ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                    style={{ height: `${heightPercent}%` }}
                    role="slider"
                    aria-label={`Hour ${hour}:00 to ${hour + 1}:00`}
                    aria-valuenow={value}
                    aria-valuemin={minValue}
                    aria-valuemax={maxValue}
                    aria-valuetext={`${formatValue(value)}`}
                    tabIndex={readOnly ? -1 : 0}
                  >
                    {/* Value label on bar for accessibility */}
                    {heightPercent > 15 && (
                      <span className="absolute inset-x-0 top-1 text-center text-[10px] font-medium text-white/80 select-none">
                        {formatValue(value)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* X-axis labels */}
        <div className="ml-10 mt-2 flex justify-between text-xs text-muted-foreground">
          <span>0</span>
          <span>6</span>
          <span>12</span>
          <span>18</span>
          <span>24</span>
        </div>
      </div>

      {/* Quick actions */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2">
          <button
            onClick={() => {
              const newHourly = new Array(24).fill(maxValue) as number[];
              setHourlyValues(newHourly);
              const timeValues = hourlyArrayToTimeValues(newHourly);
              onChange(optimizeTimeValues(timeValues));
            }}
            className="rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            All Max
          </button>
          <button
            onClick={() => {
              const newHourly = new Array(24).fill(minValue) as number[];
              setHourlyValues(newHourly);
              const timeValues = hourlyArrayToTimeValues(newHourly);
              onChange(optimizeTimeValues(timeValues));
            }}
            className="rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            All Min
          </button>
          <button
            onClick={() => {
              // 8am-6pm pattern
              const newHourly = new Array(24).fill(minValue) as number[];
              for (let h = 8; h < 18; h++) {
                newHourly[h] = maxValue;
              }
              setHourlyValues(newHourly);
              const timeValues = hourlyArrayToTimeValues(newHourly);
              onChange(optimizeTimeValues(timeValues));
            }}
            className="rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            8am-6pm
          </button>
          <button
            onClick={() => {
              // 9am-5pm pattern
              const newHourly = new Array(24).fill(minValue) as number[];
              for (let h = 9; h < 17; h++) {
                newHourly[h] = maxValue;
              }
              setHourlyValues(newHourly);
              const timeValues = hourlyArrayToTimeValues(newHourly);
              onChange(optimizeTimeValues(timeValues));
            }}
            className="rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            9am-5pm
          </button>
        </div>
      )}
    </div>
  );
}
